// src/globalErrorBanner.js

import { useContext, useEffect, useState } from "react";
import { MyContext } from "./MyContext";
import { useSWRConfig } from "swr";
import {
  captureCameBackOnline,
  captureOfflineBannerShown,
} from "./networkTelemetry";

const OFFLINE_GRACE_PERIOD = 3000;
const AUTO_DISMISS_MS = 7000;

const BANNER_STYLE = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  color: "white",
  background: "#ff7900",
  padding: 10,
  textAlign: "center",
};

const getGracePeriodMessage = (isOnline) => {
  return isOnline
    ? "Cannot reach the server - please check your connection and try again."
    : "You appear to be offline - we'll reload automatically when you reconnect.";
};

const TOAST_STYLE = {
  position: "fixed",
  bottom: 16,
  right: 16,
  zIndex: 9999,
  color: "white",
  background: "#2e7d32",
  padding: "8px 12px",
  fontSize: 13,
  lineHeight: 1.3,
  borderRadius: 8,
  boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
  maxWidth: "min(320px, calc(100vw - 24px))",
  textAlign: "left",
};

const GlobalErrorBanner = () => {
  const { globalError, setGlobalError } = useContext(MyContext);
  const { mutate } = useSWRConfig();
  const [reconnectToast, setReconnectToast] = useState(false);

  // auto-dismiss
  useEffect(() => {
    if (!globalError || globalError?.persist || globalError?.showButtons)
      return;
    if (!navigator.onLine) return;

    const timer = setTimeout(() => {
      setGlobalError(null);
    }, AUTO_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [globalError, setGlobalError]);

  // spinner → banner after grace period
  useEffect(() => {
    if (!globalError?.persist || globalError?.message) return;

    const timer = setTimeout(() => {
      const isOnline = navigator.onLine;

      setGlobalError((prev) => ({
        ...prev,
        message: getGracePeriodMessage(isOnline),
        persist: false,
        showButtons: false,
      }));

      captureOfflineBannerShown({
        source: "banner",
        reason: isOnline ? "slow_connection" : "offline",
      });
    }, OFFLINE_GRACE_PERIOD);

    return () => clearTimeout(timer);
  }, [globalError, setGlobalError]);

  // auto-reload on reconnect
  useEffect(() => {
    if (!globalError) return;

    const handleOnline = () => {
      captureCameBackOnline({
        source: "banner",
        reason: "reconnect",
      });
      if (globalError?.source === "post") {
        setGlobalError(null); // just clear – user re-saves manually
        setReconnectToast(true);
      } else if (globalError?.source === "swr") {
        setGlobalError(null); // revalidateOnReconnect in swrConfig handles the refetch
        setReconnectToast(true);
      } else if (globalError?.source === "get") {
        setGlobalError(null); // authApiRequest will retry automatically
        setReconnectToast(true);
      } else {
        window.location.reload();
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [globalError, setGlobalError]);

  // auto-dismiss reconnect toast
  useEffect(() => {
    if (!reconnectToast) return;
    const timer = setTimeout(() => setReconnectToast(false), 2500);
    return () => clearTimeout(timer);
  }, [reconnectToast]);

  if (reconnectToast) {
    return <div style={TOAST_STYLE}>Back online</div>;
  }

  if (!globalError?.message) return null;

  // note: explicitly set showButtons: true/false when calling setGlobalError
  // to avoid auto-dismiss and button visibility conflicting
  const showButtons =
    typeof globalError === "string" ? true : (globalError?.showButtons ?? true);
  // showRetry controls only the "Try Again" button — shown for timeouts and POST errors
  const showRetry =
    typeof globalError === "string" ? true : (globalError?.showRetry ?? false);

  const handleRetry = () => {
    if (!navigator.onLine) {
      setGlobalError({
        ...globalError,
        message:
          globalError?.source === "post"
            ? "Still offline – please try saving again when you reconnect."
            : "Still offline – we'll reload automatically when you reconnect.",
      });
      return;
    }
    if (globalError?.source === "post") {
      setGlobalError(null); // just clear – user re-saves manually
      return;
    }

    if (globalError?.source === "swr") {
      setGlobalError(null); // show spinner while retrying
      mutate(() => true).catch(() => {});
      return;
    }

    window.location.reload(); // fallback for non-SWR, non-POST errors
  };

  return (
    <div style={BANNER_STYLE}>
      {globalError.message}
      {showButtons && (
        <>
          {showRetry && (
            <button
              onClick={handleRetry}
              style={{ marginLeft: 10, backgroundColor: "white" }}
            >
              Try again
            </button>
          )}

          <button
            onClick={() => setGlobalError(null)}
            style={{ marginLeft: 10, backgroundColor: "white" }}
          >
            Dismiss
          </button>
        </>
      )}
    </div>
  );
};

export default GlobalErrorBanner;
